import {
  LEAD_STATUS_LABELS,
  LEAD_SOURCE_LABELS,
  COMPANY_TYPE_LABELS,
  INTEREST_PLAN_LABELS,
  FOLLOWUP_TYPE_LABELS,
  CLOSE_OUTCOME_LABELS,
  CONTACT_TYPE_LABELS,
  CLOSED_LEAD_STATUSES,
  PLAN_MONTHLY_PRICES,
  DEFAULT_PIPELINE_MONTHS,
  DEFAULT_INTEREST_PLAN,
  canonicalLeadStatus,
} from "./constants";
import { getStageForLead } from "./pipelineStages";
import { formatDateOnly, parseDateOnly } from "../../utils/formatters";

export { formatDateOnly };

const isEmptyValue = (value) =>
  value === "" || value === null || value === undefined || value === "none";

const normalizeValue = (value) =>
  typeof value === "string" ? value.trim() : value;

/**
 * Limpia el payload de un formulario: elimina vacios y normaliza strings.
 * Los arrays (p. ej. contacts) se conservan.
 */
export const cleanFormData = (data) =>
  Object.fromEntries(
    Object.entries(data)
      .map(([key, value]) => [key, Array.isArray(value) ? value : normalizeValue(value)])
      .filter(([, value]) => (Array.isArray(value) ? true : !isEmptyValue(value)))
  );

/**
 * Reduce un objeto a solo las claves de una lista de campos.
 */
export const filterFields = (data = {}, fields = []) => {
  const names = fields.map((f) => f.name);
  return Object.fromEntries(
    Object.entries(data).filter(([key]) => names.includes(key))
  );
};

/**
 * Extrae un mensaje de error legible de una respuesta axios.
 */
export const parseError = (err) => {
  if (!err) return "Error desconocido";

  const data = err.response?.data;
  if (data) {
    if (typeof data.message === "string" && data.message.trim()) {
      return data.message.replace(/^"([^"]+)"\s*/, "$1 ");
    }
    if (typeof data.error === "string" && data.error !== "true") {
      return data.error;
    }
    if (Array.isArray(data.errors) && data.errors.length) {
      return data.errors.map((e) => e.message || String(e)).join("; ");
    }
    return "Error en la solicitud";
  }

  return err.message || String(err);
};

// ===== Etiquetas =====
export const statusLabel = (v) =>
  LEAD_STATUS_LABELS[canonicalLeadStatus(v)] || LEAD_STATUS_LABELS[v] || v || "—";
export const sourceLabel = (v) => LEAD_SOURCE_LABELS[v] || "—";
export const companyTypeLabel = (v) => COMPANY_TYPE_LABELS[v] || "—";
export const interestPlanLabel = (v) => INTEREST_PLAN_LABELS[v] || "—";
export const followUpTypeLabel = (v) => FOLLOWUP_TYPE_LABELS[v] || v || "—";
export const outcomeLabel = (v) => CLOSE_OUTCOME_LABELS[v] || "—";
export const contactTypeLabel = (v) => CONTACT_TYPE_LABELS[v] || v || "—";

/**
 * Número seguro para KPIs/moneda. NaN, ±Infinity y no-numéricos → fallback.
 */
export const safeNumber = (value, fallback = 0) => {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
};

/** Prob. de cierre acotada a 0–100. */
export const safeProbability = (value) =>
  Math.min(100, Math.max(0, safeNumber(value, 0)));

/** Precio mensual del plan; null si es Enterprise / desconocido. */
export const monthlyPriceForPlan = (interestPlan) => {
  if (!interestPlan) return null;
  const price = PLAN_MONTHLY_PRICES[interestPlan];
  return Number.isFinite(price) ? price : null;
};

/** Ancla de cartera: 1 mes Starter. */
export const defaultPipelineValue = () =>
  safeNumber(PLAN_MONTHLY_PRICES[DEFAULT_INTEREST_PLAN], 1999) * DEFAULT_PIPELINE_MONTHS;

/**
 * Valor de pipeline: 1 mes del plan.
 * Sin plan ni monto → Starter (evita $0).
 */
export const resolvePotentialValue = (interestPlan, potentialValue) => {
  const monthly = monthlyPriceForPlan(interestPlan);
  if (monthly != null) return monthly * DEFAULT_PIPELINE_MONTHS;
  const n = safeNumber(potentialValue, 0);
  if (n > 0) return n;
  return defaultPipelineValue();
};

/**
 * Valor a mostrar en listados/kanban/KPIs locales.
 * Usa el monto guardado, el del plan, o Starter si viene vacío/0.
 */
export const leadPipelineValue = (lead) => {
  const stored = safeNumber(lead?.potentialValue, 0);
  if (stored > 0) return stored;
  const fromPlan = monthlyPriceForPlan(lead?.interestPlan);
  if (fromPlan != null) return fromPlan * DEFAULT_PIPELINE_MONTHS;
  return defaultPipelineValue();
};

/** Valor esperado de un lead: monto × tasa de la etapa del embudo. */
export const leadExpectedValue = (lead) => {
  const value = leadPipelineValue(lead);
  const stage = getStageForLead(lead);
  const rate = safeProbability(stage?.expectedRate);
  const expected = (value * rate) / 100;
  return Number.isFinite(expected) ? expected : 0;
};

/**
 * Construye la lista de contactos a partir del lead (array o campos legacy).
 */
export const contactsFromLead = (lead = {}) => {
  if (Array.isArray(lead.contacts) && lead.contacts.length > 0) {
    return lead.contacts.map((c) => ({
      type: c.type || "OTHER",
      value: c.value || "",
      label: c.label || "",
      isPrimary: Boolean(c.isPrimary),
    }));
  }
  const list = [];
  if (lead.email) list.push({ type: "EMAIL", value: lead.email, label: "", isPrimary: true });
  if (lead.phone) list.push({ type: "PHONE", value: lead.phone, label: "", isPrimary: true });
  if (lead.mobile) list.push({ type: "MOBILE", value: lead.mobile, label: "", isPrimary: false });
  if (lead.whatsapp) list.push({ type: "WHATSAPP", value: lead.whatsapp, label: "", isPrimary: false });
  return list;
};

/** Primer valor de un tipo de contacto (o legacy). */
export const primaryContactValue = (lead, type) => {
  const fromArr = (lead?.contacts || []).find((c) => c.type === type && c.value);
  if (fromArr) return fromArr.value;
  const legacy = { EMAIL: "email", PHONE: "phone", MOBILE: "mobile", WHATSAPP: "whatsapp" };
  return lead?.[legacy[type]] || "";
};

export const isLeadClosed = (lead) =>
  !lead?.isActive ||
  Boolean(lead?.outcome) ||
  CLOSED_LEAD_STATUSES.includes(lead?.status);

// ===== Formateadores =====
export const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const n = safeNumber(value, NaN);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0,
  });
};

const APP_TIMEZONE = "America/Mexico_City";

/**
 * Fecha de calendario (timestamps). Para campos solo-día usar formatDateOnly.
 */
export const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: APP_TIMEZONE,
  });
};

export const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: APP_TIMEZONE,
  });
};

export const relativeFromNow = (value) => {
  if (!value) return "";
  const d = new Date(value).getTime();
  const diff = d - Date.now();
  const days = Math.round(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return "hoy";
  if (days === 1) return "mañana";
  if (days === -1) return "ayer";
  if (days > 1) return `en ${days} días`;
  return `hace ${Math.abs(days)} días`;
};

export const userLabel = (u) => {
  if (!u) return "Sin asignar";
  if (typeof u === "string") return u;
  return u.name || u.email || "Sin asignar";
};

/**
 * Devuelve true si la fecha (proximo seguimiento) esta vencida.
 * Compara por día calendario, no por instante UTC.
 */
export const isOverdue = (value) => {
  if (!value) return false;
  const followUpDay = parseDateOnly(value);
  if (!followUpDay) return false;
  const todayLocal = calendarToday();
  return followUpDay < todayLocal;
};

function calendarToday() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: APP_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const y = Number(parts.find((p) => p.type === "year").value);
  const m = Number(parts.find((p) => p.type === "month").value);
  const d = Number(parts.find((p) => p.type === "day").value);
  return new Date(y, m - 1, d);
}

function calendarDaysBetween(fromDate, toDate = calendarToday()) {
  if (!fromDate) return null;
  const a = parseDateOnly(fromDate) || (fromDate instanceof Date ? fromDate : new Date(fromDate));
  const b = parseDateOnly(toDate) || (toDate instanceof Date ? toDate : new Date(toDate));
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  const start = new Date(a.getFullYear(), a.getMonth(), a.getDate());
  const end = new Date(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((end - start) / (1000 * 60 * 60 * 24));
}

/** yyyy-mm-dd a N días desde hoy (calendario CDMX). */
export const dateOnlyPlusDays = (days = 1) => {
  const t = calendarToday();
  t.setDate(t.getDate() + days);
  const mm = String(t.getMonth() + 1).padStart(2, "0");
  const dd = String(t.getDate()).padStart(2, "0");
  return `${t.getFullYear()}-${mm}-${dd}`;
};

export const formatRelativeShort = (value) => {
  if (!value) return "";
  const diff = Date.now() - new Date(value).getTime();
  const mins = Math.round(diff / 60000);
  if (Math.abs(mins) < 1) return "ahora";
  if (Math.abs(mins) < 60) return mins > 0 ? `hace ${mins} min` : `en ${Math.abs(mins)} min`;
  const hours = Math.round(mins / 60);
  if (Math.abs(hours) < 24) return hours > 0 ? `hace ${hours} h` : `en ${Math.abs(hours)} h`;
  return relativeFromNow(value);
};

/**
 * Indicador de urgencia para filas de cartera (no cierra el lead).
 */
export const leadHeat = (lead) => {
  if (!lead || isLeadClosed(lead)) return null;
  if (lead.priority === "URGENT") return { key: "urgent", label: "Urgente", tone: "urgent" };
  const stage = getStageForLead(lead);
  if (isOverdue(lead.nextFollowUpAt)) return { key: "overdue", label: "Vencido", tone: "danger" };
  if (!lead.nextFollowUpAt) return { key: "no-next", label: "Sin próximo", tone: "warn" };

  const untilNext = calendarDaysBetween(calendarToday(), parseDateOnly(lead.nextFollowUpAt));
  if (untilNext === 0) return { key: "today", label: "Hoy", tone: "info" };
  if (untilNext === 1) return { key: "tomorrow", label: "Mañana", tone: "info" };

  const silent = calendarDaysBetween(lead.lastContactAt || lead.createdAt);
  if (silent != null && silent >= 3) {
    return { key: "silent", label: `${silent}d sin contacto`, tone: "warn" };
  }
  if (stage?.key === "closing" && silent != null && silent >= 5) {
    return { key: "proposal", label: "Propuesta en espera", tone: "warn" };
  }
  if (stage?.key === "demo" && untilNext === 1) {
    return { key: "demo", label: "Demo mañana", tone: "info" };
  }
  return null;
};
